import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePagedData } from "@/hooks/usePagedData";
import { useSite } from "@/context/SiteContext";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { KpiCard } from "@/components/common/KpiCard";
import { Loader } from "@/components/common/Loader";
import { Pager, SearchInput } from "@/components/common/Pager";
import { eur, num, shortDate } from "@/lib/format";
import api from "@/lib/api";
import { ShoppingCart, Plus, Truck } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

export default function Sales() {
  const { items: orders, stats, total, pages, page, setPage, q, setQ, loading, reload } = usePagedData("/sales-orders");
  const { items: customers } = usePagedData("/customers", 200);
  const { items: products } = usePagedData("/materials", 200);
  const { sites, activeSite } = useSite();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_code: "", material_code: "", qty: "", unit_price: "", req_delivery: "", site_id: "" });

  const nextStatus = { OPEN: "IN_DELIVERY", IN_DELIVERY: "DELIVERED", DELIVERED: "INVOICED" };
  const advance = async (e, o) => {
    e.stopPropagation();
    const next = nextStatus[o.status];
    if (!next) return;
    await api.patch(`/sales-orders/${o.id}/status?status=${next}`);
    toast.success(`${o.order_no} → ${next.replace(/_/g, " ")}`);
    reload();
  };

  const create = async () => {
    const cust = customers?.find((c) => c.customer_code === form.customer_code);
    const prod = products?.find((p) => p.material_code === form.material_code);
    try {
      await api.post("/sales-orders", {
        site_id: form.site_id || (activeSite !== "GROUP" ? activeSite : sites[0]?.id),
        customer_code: form.customer_code, customer_name: cust?.name || form.customer_code,
        material_code: form.material_code, material_name: prod?.name || form.material_code,
        qty: Number(form.qty), unit_price: Number(form.unit_price), req_delivery: form.req_delivery,
      });
      toast.success("Sales order created");
      setOpen(false); setForm({ customer_code: "", material_code: "", qty: "", unit_price: "", req_delivery: "", site_id: "" });
      reload();
    } catch (e) { toast.error("Failed to create order"); }
  };

  const columns = [
    { key: "order_no", label: "Order", render: (r) => <span className="font-mono text-xs text-[#8B5CF6]">{r.order_no}</span> },
    { key: "customer_name", label: "Sold-To", render: (r) => (
      <div><div className="text-[#32363a]">{r.customer_name}</div><div className="font-mono text-[10px] text-[#6a6d70]">{r.customer_code}</div></div>) },
    { key: "ship_to", label: "Ship-To", render: (r) => <span className="text-xs text-[#454b52]">{r.partners?.ship_to?.name || r.customer_name}</span> },
    { key: "material_name", label: "Product", render: (r) => (
      <div><div className="text-[#32363a]">{r.material_name}</div><div className="font-mono text-[10px] text-[#6a6d70]">{num(r.qty)} {r.uom} · {(r.items?.length || 1)} item(s)</div></div>) },
    { key: "net_value", label: "Net Value", render: (r) => <span className="font-mono text-emerald-600">{eur(r.net_value)}</span> },
    { key: "channel", label: "Channel", render: (r) => <span className="font-mono text-[10px] text-[#6a6d70]">{r.channel}</span> },
    { key: "order_date", label: "Order Date", render: (r) => shortDate(r.order_date) },
    { key: "status", label: "Status", badge: true },
    { key: "actions", label: "", render: (r) => (nextStatus[r.status] ? (
      <button data-testid={`advance-so-${r.order_no}`} onClick={(e) => advance(e, r)}
        className="inline-flex items-center gap-1 rounded-md border border-[#c8cfda] bg-white px-2 py-1 text-[11px] text-[#32363a] hover:border-[#8B5CF6]">
        <Truck size={13} /> Progress</button>) : <span className="font-mono text-[10px] text-[#89919a]">closed</span>) },
  ];

  return (
    <div>
      <PageHeader title="Sales & Distribution" subtitle="Sales orders derived from real customer sales history, with partner functions, line items, pricing conditions and delivery pipeline. Click a row to open the order." icon={ShoppingCart} accent="#8B5CF6">
        <div className="flex items-center gap-2">
          <button data-testid="new-so-va01-btn" onClick={() => navigate("/sales/new")} className="inline-flex items-center gap-2 rounded-md bg-[#8B5CF6] px-4 py-2 text-sm font-semibold text-white hover:bg-[#7c3aed]"><Plus size={16} /> New Sales Order (EPS-101)</button>
        </div>
      </PageHeader>

      <div className="mb-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Order Value" value={eur(stats.net_value || 0)} accent="#8B5CF6" />
        <KpiCard label="Total Orders" value={total.toLocaleString()} accent="#8B5CF6" />
        <KpiCard label="Open" value={stats.open || 0} accent="#e9730c" />
        <KpiCard label="EDI Orders" value={stats.edi || 0} sub="auto-received" accent="#0284C7" />
      </div>

      <div className="mb-3"><SearchInput testId="sales-search" value={q} onChange={setQ} placeholder="Search orders by number, customer, product…" /></div>
      {loading && orders.length === 0 ? <Loader /> : (
        <DataTable testId="sales-orders-table" columns={columns} rows={orders} onRowClick={(r) => navigate(`/sales/${r.id}`)} />
      )}
      <Pager testId="sales-pager" page={page} pages={pages} total={total} pageSize={50} onPage={setPage} />
    </div>
  );
}
